import { Button, Group, LoadingOverlay, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmActionButton } from '@/components/ConfirmActionButton';
import {
  adminAssignRoute,
  adminReassignRoute,
  adminSelectExtra,
  adminUnassignRoute,
  adminWithdrawExtra,
} from '@/services';
import type { AppUser, RouteAssignment, RouteDefinition } from '@/types';

interface ManualAssignModalProps {
  opened: boolean;
  onClose: () => void;
  route: RouteDefinition | null;
  date: string;
  currentUserId: string | null;
  users: AppUser[];
  extraAssignments: RouteAssignment[];
}

/**
 * Admin control for a single route+day: assign it to someone (if free),
 * reassign it to someone else (if already held), or drop the current
 * holder entirely. One modal handles all three since they're mutually
 * exclusive actions on the same slot.
 */
export const ManualAssignModal = ({
  opened,
  onClose,
  route,
  date,
  currentUserId,
  users,
  extraAssignments,
}: ManualAssignModalProps) => {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const userOptions = users
    .filter((user) => !user.disabled)
    .map((user) => ({ value: user.uid, label: user.displayName }));

  const handleClose = () => {
    setSelectedUserId(null);
    onClose();
  };

  const handleAssign = async () => {
    if (!(route && selectedUserId)) return;
    setSubmitting(true);
    try {
      if (route.category === 'extra') {
        await adminSelectExtra(route.id, date, selectedUserId);
      } else if (currentUserId) {
        await adminReassignRoute(route.id, date, selectedUserId);
      } else {
        await adminAssignRoute(route.id, date, selectedUserId);
      }
      notifications.show({
        color: 'signal',
        message: t('modals.manualAssign.notifications.updated', {
          name:
            route.category === 'extra'
              ? t('routeCategories.extraHours', { hours: route.extraDurationHours })
              : route.name,
        }),
      });
      handleClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        message:
          error instanceof Error
            ? error.message
            : t('modals.manualAssign.notifications.assignFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    if (!route || route.category === 'extra') return;
    setSubmitting(true);
    try {
      await adminUnassignRoute(route.id, date);
      notifications.show({
        color: 'signal',
        message: t('modals.manualAssign.notifications.unassigned', { name: route.name }),
      });
      handleClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        message:
          error instanceof Error
            ? error.message
            : t('modals.manualAssign.notifications.unassignFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawExtra = async (assignmentId: string) => {
    setSubmitting(true);
    try {
      await adminWithdrawExtra(assignmentId);
      notifications.show({
        color: 'signal',
        message: t('modals.manualAssign.extraWithdrawn'),
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        message:
          error instanceof Error
            ? error.message
            : t('modals.manualAssign.notifications.unassignFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activeExtraAssignments = extraAssignments.filter(
    (assignment) => assignment.kind === 'extra' && assignment.status === 'assigned',
  );

  return (
    <Modal
      centered
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
      onClose={handleClose}
      opened={opened}
      title={
        <Text c="signal.7" ff="monospace" fw={700} size="xs" style={{ letterSpacing: '0.2em' }}>
          {route?.category === 'extra'
            ? t('routeCategories.extraHours', { hours: route.extraDurationHours }).toUpperCase()
            : route?.name.toUpperCase()}
        </Text>
      }
      withCloseButton={!submitting}
    >
      <Stack gap="md" pos="relative">
        <LoadingOverlay overlayProps={{ blur: 1 }} visible={submitting} zIndex={1000} />
        {route?.category === 'extra' && (
          <Stack gap="xs">
            <Text c="ink.6" ff="monospace" size="xs">
              {t('modals.manualAssign.extraSelections', { count: activeExtraAssignments.length })}
            </Text>
            {activeExtraAssignments.map((assignment) => (
              <Group justify="space-between" key={assignment.id}>
                <Text size="sm">
                  {users.find((user) => user.uid === assignment.userId)?.displayName ??
                    t('admin.userDetail.fallbackName')}
                </Text>
                <ConfirmActionButton
                  confirmLabel={t('modals.manualAssign.confirmUnassign')}
                  label={t('modals.manualAssign.unassign')}
                  noShadow
                  onConfirm={() => handleWithdrawExtra(assignment.id)}
                />
              </Group>
            ))}
          </Stack>
        )}
        <Select
          clearable
          data={userOptions}
          label={t('modals.manualAssign.assignTo')}
          labelProps={{ style: { letterSpacing: '0.1em', fontSize: '0.6875rem', fontWeight: 700 } }}
          onChange={setSelectedUserId}
          placeholder={t('modals.manualAssign.placeholder')}
          searchable
          value={selectedUserId}
        />
        <Button
          disabled={!selectedUserId}
          fullWidth
          loading={submitting}
          onClick={handleAssign}
          style={{ boxShadow: 'none', fontWeight: 700, letterSpacing: '0.06em' }}
        >
          {route?.category === 'extra'
            ? t('modals.manualAssign.addExtra')
            : currentUserId
              ? t('modals.manualAssign.reassign')
              : t('modals.manualAssign.assign')}
        </Button>
        {currentUserId && route?.category !== 'extra' && (
          <ConfirmActionButton
            confirmLabel={t('modals.manualAssign.confirmUnassign')}
            fullWidth
            label={t('modals.manualAssign.unassign')}
            noShadow
            onConfirm={handleUnassign}
          />
        )}
      </Stack>
    </Modal>
  );
};
